/**
 * Separates hashtags that were added via the tag selector (stored in the
 * `hashtags` array on the post) from the caption text.
 *
 * The API's `appendHashtagsToText` appends `#tag` tokens to the end of the
 * caption `BodyText` for every separately-selected hashtag that isn't already
 * present. On the read side, `post.text` therefore contains the caption plus
 * those appended tags, and `post.hashtags` holds the separately-stored list.
 *
 * This function strips the trailing `#tag` tokens from `post.text` that match
 * `post.hashtags`, so the caption can be rendered as the user typed it and the
 * separately-added tags can be shown on a new line below. Tags the user typed
 * inline are left untouched — `appendHashtagsToText` skips a tag that is
 * already in the text, so it never appears twice, and a tag that was typed
 * inline but also selected separately will only be stripped if it sits at the
 * very end of the text (where the API would have appended it).
 */

import {
  splitSeparateHashtags as splitPostHashtags,
} from '@wlb/shared/utils/postDisplay';
import { formatHashtag } from './hashtags';

export interface SeparateHashtagsResult {
  /** The caption text with separately-stored hashtags stripped from the end. */
  caption: string;
  /** The stripped hashtags as `#tag` strings, in their original trailing order. */
  separateHashtags: string[];
}

/**
 * Strips trailing `#tag` tokens from `text` that match any entry in
 * `hashtags` (case-insensitive). Returns the remaining caption and the
 * selected tags, each with exactly one `#`.
 *
 * The shared helper preserves the untouched caption substring instead of
 * rebuilding it from whitespace-delimited tokens. It also returns selected
 * hashtags when an optimistic create/update has not appended them to `text`
 * yet, so feed cards and API-refetched profile cards render the same way.
 */
export function splitSeparateHashtags(
  text: string,
  hashtags: string[] | null | undefined,
): SeparateHashtagsResult {
  if (!hashtags?.length) return { caption: text, separateHashtags: [] };

  const { caption, separateHashtags } = splitPostHashtags(text, hashtags);
  return {
    caption,
    separateHashtags: separateHashtags.map(formatHashtag),
  };
}
